/**
 * Bränslebolag per organisation
 * Listar kundnummer per bränslebolag med logotyp; lägg till / ta bort
 */

import { useEffect, useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import { organizationsService } from '@/services/organizations/organizationsService';
import type { EntityId } from '@/types/ids';
import toast from 'react-hot-toast';

export interface FuelAccountItem {
  id: number;
  fuel_company_id: number;
  customer_number: string;
  fuel_company_name?: string;
  fuel_company_logo_url?: string | null;
}

interface FuelCompanyOption {
  id: number;
  name: string;
  logo_url: string | null;
}

interface OrganizationFuelAccountsProps {
  organizationId: EntityId;
}

export default function OrganizationFuelAccounts({ organizationId }: OrganizationFuelAccountsProps) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<FuelAccountItem[]>([]);
  const [fuelCompanies, setFuelCompanies] = useState<FuelCompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addFuelCompanyId, setAddFuelCompanyId] = useState<number | ''>('');
  const [addCustomerNumber, setAddCustomerNumber] = useState('');

  const load = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const [accountsRes, companiesRes] = await Promise.all([
        organizationsService.getOrganizationFuelAccounts(organizationId),
        organizationsService.getFuelCompanies(),
      ]);
      setAccounts(accountsRes);
      setFuelCompanies(companiesRes);
    } catch (e: any) {
      toast.error(e.response?.data?.error ?? e.message ?? t('organizations.fuelAccounts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [organizationId]);

  const saveAccounts = async (newAccounts: { fuel_company_id: number; customer_number: string }[]) => {
    setSaving(true);
    try {
      const result = await organizationsService.setOrganizationFuelAccounts(organizationId, newAccounts);
      setAccounts(result.accounts as FuelAccountItem[]);
      if (result.linkedTransactions > 0) {
        toast.success(
          t('organizations.fuelAccounts.savedWithLinks', { count: result.linkedTransactions })
        );
      } else {
        toast.success(t('organizations.fuelAccounts.saved'));
      }
      setAddFuelCompanyId('');
      setAddCustomerNumber('');
    } catch (e: any) {
      toast.error(e.response?.data?.error ?? e.message ?? t('organizations.fuelAccounts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = (id: number) => {
    const next = accounts.filter((a) => a.id !== id).map((a) => ({ fuel_company_id: a.fuel_company_id, customer_number: a.customer_number }));
    saveAccounts(next);
  };

  const handleAdd = () => {
    const companyId = addFuelCompanyId === '' ? 0 : Number(addFuelCompanyId);
    const customerNumber = addCustomerNumber.trim();
    if (!companyId || !customerNumber) {
      toast.error(t('organizations.fuelAccounts.fillAll'));
      return;
    }
    const next = [
      ...accounts.map((a) => ({ fuel_company_id: a.fuel_company_id, customer_number: a.customer_number })),
      { fuel_company_id: companyId, customer_number: customerNumber },
    ];
    saveAccounts(next);
  };

  if (loading) {
    return <p className="text-textTertiary text-sm">{t('common.loading')}</p>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-textDefault">{t('organizations.fuelCompanies.tabTitle')}</h3>

      {/* Lista med logotyp + kundnummer */}
      <div className="rounded-lg border border-border bg-bgElevated divide-y divide-border">
        {accounts.length === 0 ? (
          <p className="p-4 text-textSecondary text-sm">{t('organizations.fuelAccounts.empty')}</p>
        ) : (
          accounts.map((acc) => (
            <div
              key={acc.id}
              className="flex items-center gap-3 p-3"
            >
              {acc.fuel_company_logo_url ? (
                <img
                  src={acc.fuel_company_logo_url}
                  alt=""
                  className="w-10 h-10 object-contain rounded border border-border flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded border border-border bg-bgContainer flex-shrink-0 flex items-center justify-center text-textTertiary text-xs">
                  —
                </div>
              )}
              <div className="flex-1 min-w-0">
                <span className="font-medium text-textDefault">{acc.fuel_company_name ?? '-'}</span>
                <span className="text-textSecondary ml-2">{acc.customer_number}</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(acc.id)}
                disabled={saving}
                className="text-sm text-red-600 hover:underline disabled:opacity-50"
              >
                {t('common.remove')}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Lägg till */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[180px]">
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('organizations.fuelAccounts.fuelCompany')}
          </label>
          <select
            value={addFuelCompanyId === '' ? '' : addFuelCompanyId}
            onChange={(e) => setAddFuelCompanyId(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault text-sm"
          >
            <option value="">{t('organizations.fuelAccounts.selectCompany')}</option>
            {fuelCompanies.map((fc) => (
              <option key={fc.id} value={fc.id}>
                {fc.name}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[140px]">
          <label className="block text-xs font-medium text-textSecondary mb-1">
            {t('organizations.fuelAccounts.customerNumber')}
          </label>
          <input
            type="text"
            value={addCustomerNumber}
            onChange={(e) => setAddCustomerNumber(e.target.value)}
            placeholder={t('organizations.fuelAccounts.customerNumberPlaceholder')}
            className="w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault text-sm"
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving || !addFuelCompanyId || !addCustomerNumber.trim()}
          className="px-3 py-2 text-sm bg-bgPrimary text-textLight rounded-lg hover:opacity-90 disabled:opacity-50"
        >
          {t('organizations.fuelAccounts.add')}
        </button>
      </div>
    </div>
  );
}
